// packages/luminicad-core/src/services/aiCadExecutionService.ts
// Copyright 2024 the LuminiCAD authors. All rights reserved. AGPL-3.0 license.

import {
    I18n,
    IApplication,
    IEdge,
    IService,
    IShape,
    Logger,
    Plane,
    PubSub,
    Ray,
    Result,
    ShapeType,
    Transaction,
    XYZ,
} from "luminicad-core";
// Import the specific node type we are integrating first.
import { ArcNode } from "../../../luminicad/src/bodys/arc"; // Adjust path if necessary based on final location
// Import BooleanNode for potential result storage and type checking.
import { BooleanNode } from "../../../luminicad/src/bodys/boolean"; // Adjust path if necessary
// Import base shape node for type checking in helper.
import { EditableShapeNode, ShapeNode } from "luminicad-core"; // Only ShapeNode is needed for the check
// Import BoxNode.
import { BoxNode } from "../../../luminicad/src/bodys/box"; // Adjust path if necessary
// Import CircleNode.
import { CircleNode } from "../../../luminicad/src/bodys/circle"; // Adjust path if necessary
// Import ConeNode.
import { ConeNode } from "../../../luminicad/src/bodys/cone"; // Adjust path if necessary
// Import CylinderNode.
import { CylinderNode } from "../../../luminicad/src/bodys/cylinder"; // Adjust path if necessary
// Import EllipseNode.
import { EllipseNode } from "../../../luminicad/src/bodys/ellipse"; // Adjust path if necessary
// Import FaceNode.
import { FaceNode } from "../../../luminicad/src/bodys/face"; // Adjust path if necessary
// Import FuseNode.
import { FuseNode } from "../../../luminicad/src/bodys/fuse"; // Adjust path if necessary
// Import LineNode.
import { LineNode } from "../../../luminicad/src/bodys/line"; // Adjust path if necessary
// Import PolygonNode.
import { PolygonNode } from "../../../luminicad/src/bodys/polygon"; // Adjust path if necessary
// Import PrismNode.
import { PrismNode } from "../../../luminicad/src/bodys/prism"; // Adjust path if necessary
// Import PyramidNode.
import { PyramidNode } from "../../../luminicad/src/bodys/pyramid"; // Adjust path if necessary
// Import RectNode.
import { RectNode } from "../../../luminicad/src/bodys/rect"; // Adjust path if necessary
// Import RevolvedNode.
import { RevolvedNode } from "../../../luminicad/src/bodys/revolve"; // Adjust path if necessary
// Import SweepedNode.
import { SweepedNode } from "../../../luminicad/src/bodys/sweep"; // Adjust path if necessary
// Import SphereNode.
import { SphereNode } from "../../../luminicad/src/bodys/sphere"; // Adjust path if necessary
// Import WireNode.
import { WireNode } from "../../../luminicad/src/bodys/wire"; // Adjust path if necessary
// Import necessary Material classes for material assignment.
import { MaterialCategory, MaterialLibrary } from "luminicad-core";
// Import AI_CONFIG to access the CadQuery service URL.
import { AI_CONFIG } from "../config/config";
// Import Constants for database name.
import { Constants } from "luminicad-core";

/**
 * AiCadExecutionService
 *
 * Executes AI-generated TypeScript code specifically designed to interact with
 * LuminiCAD's document model, primarily using the high-level 'bodys' classes.
 */
export class AiCadExecutionService implements IService {
    private app!: IApplication;
    // Added variable storage for more complex AI scripts.
    private storedVariables: Map<string, any> = new Map();

    // Base64 helper functions using only browser-native methods (btoa/atob) for cross-environment compatibility
    // Assumes standard Web APIs are available.
    private static base64Encode(str: string): string {
        // Use `encodeURIComponent` to handle multi-byte chars, then `unescape` to get binary string, then `btoa`
        try {
            // Note: unescape is deprecated but widely supported; common pattern for btoa with UTF-8
            return btoa(unescape(encodeURIComponent(str)));
        } catch (e: unknown) {
            // Explicitly type catch variable as unknown
            let errorMessage = "Unknown encoding error";
            if (e instanceof Error) {
                errorMessage = e.message;
            }
            Logger.error("base64Encode error:", errorMessage);
            // Re-throw with a more specific error message
            throw new Error(`Failed to base64 encode string: ${errorMessage}`);
        }
    }

    private static base64DecodeToBytes(base64: string): Uint8Array {
        try {
            const binaryString = atob(base64);
            const length = binaryString.length;
            const bytes = new Uint8Array(length);
            for (let i = 0; i < length; i++) {
                bytes[i] = binaryString.charCodeAt(i);
            }
            return bytes;
        } catch (e: unknown) {
            // Explicitly type catch variable as unknown
            let errorMessage = "Unknown decoding error";
            if (e instanceof Error) {
                errorMessage = e.message;
            }
            Logger.error("base64DecodeToBytes error:", errorMessage);
            throw new Error(`Failed to decode base64 string: ${errorMessage}`);
        }
    }

    // --- IService Implementation ---

    /**
     * Registers the service with the main application instance.
     * @param app The main application instance.
     */
    register(app: IApplication): void {
        // Store the application instance for later use (e.g., accessing active document).
        this.app = app;
    }

    /**
     * Starts the service. Currently does nothing specific on start.
     */
    start(): void {
        Logger.info(`${AiCadExecutionService.name} started`);
    }

    /**
     * Stops the service. Currently does nothing specific on stop.
     */
    stop(): void {
        // No cleanup needed for this version.
    }

    // --- Public Execution API ---

    /**
     * Executes AI-generated TypeScript code within a controlled context.
     *
     * @param aiCode The string containing the TypeScript code to execute.
     * @param maintainVariables Optional flag to keep variables between calls. Defaults to false.
     * @returns A Result object containing the output of the executed code (if any)
     *          or an error message string.
     */
    async executeCode(aiCode: string, maintainVariables: boolean = false): Promise<Result<any, string>> {
        // Log entry into executeCode to track multiple calls.
        Logger.warn(
            `AiCadExecutionService: executeCode called. Input length: ${aiCode.length}. First 100 chars: ${aiCode.substring(0, 100)}...`,
        );

        // Get the currently active document from the application. Execution requires an active document.
        const doc = this.app.activeView?.document;
        if (!doc) {
            Logger.warn("AiCadExecutionService: No active document found.");
            return Result.err("No active document found.");
        }

        // Clear variables if not maintaining state across calls.
        if (!maintainVariables) {
            this.storedVariables.clear();
        }

        // STEP 1.2 - Attempt to parse aiCode as JSON for CadQuery delegation.
        try {
            const parsedAiCode = JSON.parse(aiCode);
            // Check if the parsed object has tool === "cadquery".
            if (parsedAiCode && parsedAiCode.tool === "cadquery") {
                // Wrap the entire CadQuery delegation logic in its own try...catch block.
                try {
                    // This is a CadQuery directive. Proceed with CadQuery delegation logic (To be implemented in Task 3).
                    Logger.info(
                        "AiCadExecutionService: Detected CadQuery directive. Delegating to CadQuery service.",
                    );
                    // --------------- START OF CADQUERY DELEGATION LOGIC (TASK 1.3) ---------------
                    // Extract script, input_files, and expected_output_format from the parsed JSON.
                    const {
                        script,
                        input_files: inputFiles,
                        expected_output_format: expectedOutputFormat,
                    } = parsedAiCode;

                    // Validate required script parameter.
                    if (!script || typeof script !== "string") {
                        Logger.error(
                            "AiCadExecutionService: CadQuery directive is missing 'script' or it's not a string.",
                        );
                        return Result.err("CadQuery directive error: 'script' is missing or invalid.");
                    }

                    // Initialize payload for the CadQuery service.
                    const cadQueryServicePayloadInputFiles: { filename: string; content_base64: string }[] =
                        [];

                    // Process input_files if provided.
                    if (inputFiles && Array.isArray(inputFiles)) {
                        for (const inputFile of inputFiles) {
                            if (!inputFile.variable_name || !inputFile.output_filename_for_script) {
                                Logger.warn(
                                    "AiCadExecutionService: Skipping invalid input_file entry in CadQuery directive:",
                                    inputFile,
                                );
                                continue;
                            }
                            // Retrieve the stored LuminiCAD object.
                            const luminiCadObject = this.storedVariables.get(inputFile.variable_name);
                            if (!luminiCadObject) {
                                throw new Error(
                                    `Variable '${inputFile.variable_name}' not found in storedVariables.`,
                                );
                            }

                            // Ensure it's a ShapeNode and get IShape.
                            if (!(luminiCadObject instanceof ShapeNode)) {
                                throw new Error(`Variable '${inputFile.variable_name}' is not a ShapeNode.`);
                            }
                            const shapeToExportResult = luminiCadObject.shape;
                            if (!shapeToExportResult.isOk) {
                                throw new Error(
                                    `Error getting shape from '${inputFile.variable_name}': ${shapeToExportResult.error}`,
                                );
                            }
                            const shapeToExport = shapeToExportResult.value;

                            if (!this.app.shapeConverter || !("convertToSTEP" in this.app.shapeConverter)) {
                                Logger.error(
                                    "AiCadExecutionService: IShapeConverter with convertToSTEP is not available on app.",
                                );
                                return Result.err(
                                    "CadQuery preprocessing error: Shape converter not available.",
                                );
                            }
                            const stepResult = this.app.shapeConverter.convertToSTEP(shapeToExport);
                            if (!stepResult.isOk) {
                                Logger.error(
                                    "AiCadExecutionService: Failed to convert IShape to STEP:",
                                    stepResult.error,
                                );
                                return Result.err(
                                    `CadQuery preprocessing error: Failed to convert '${inputFile.variable_name}' to STEP: ${stepResult.error}`,
                                );
                            }
                            const stepDataString = stepResult.value;

                            const base64EncodedStepString =
                                AiCadExecutionService.base64Encode(stepDataString);

                            cadQueryServicePayloadInputFiles.push({
                                filename: inputFile.output_filename_for_script,
                                content_base64: base64EncodedStepString,
                            });
                            Logger.info(
                                `AiCadExecutionService: Prepared input file '${inputFile.output_filename_for_script}' from variable '${inputFile.variable_name}'.`,
                            );
                        }
                    }

                    const cadQueryServiceUrl = AI_CONFIG.CADQUERY_SERVICE_URL;
                    const payload = {
                        script: script,
                        input_files: cadQueryServicePayloadInputFiles,
                        output_format: expectedOutputFormat || "step",
                    };

                    Logger.info(
                        `AiCadExecutionService: Preparing to send POST request to CadQuery service at URL: ${cadQueryServiceUrl}`,
                    );
                    Logger.info("AiCadExecutionService: CadQuery service request payload:", payload);

                    let response;
                    try {
                        Logger.info(
                            `AiCadExecutionService: Executing fetch POST to ${cadQueryServiceUrl}...`,
                        );
                        response = await fetch(cadQueryServiceUrl, {
                            method: "POST",
                            headers: { "Content-Type": "application/json" },
                            body: JSON.stringify(payload),
                        });
                        Logger.info(
                            `AiCadExecutionService: Received response from ${cadQueryServiceUrl} with status: ${response.status}`,
                        );
                    } catch (networkError: any) {
                        Logger.error(
                            "AiCadExecutionService: Network error calling CadQuery service:",
                            networkError,
                        );
                        return Result.err(`CadQuery service network error: ${networkError.message}`);
                    }

                    if (!response.ok) {
                        const errorText = await response.text();
                        Logger.error(
                            `AiCadExecutionService: CadQuery service returned error ${response.status}:`,
                            errorText,
                        );
                        try {
                            const errorJson = JSON.parse(errorText);
                            return Result.err(
                                `CadQuery service error (${response.status}): ${errorJson.message || errorText}. Logs: ${JSON.stringify(errorJson.logs)}`,
                            );
                        } catch {
                            return Result.err(`CadQuery service error (${response.status}): ${errorText}`);
                        }
                    }

                    const resultData = await response.json();
                    Logger.info(
                        "AiCadExecutionService: Parsed JSON response from CadQuery service:",
                        resultData,
                    );

                    if (resultData.status === "error") {
                        Logger.error(
                            "AiCadExecutionService: CadQuery service reported an execution error:",
                            resultData.message,
                            resultData.logs,
                        );
                        return Result.err(
                            `CadQuery script execution error: ${resultData.message}. Logs: ${JSON.stringify(resultData.logs)}`,
                        );
                    }

                    if (
                        resultData.status === "success" &&
                        resultData.output_files &&
                        resultData.output_files.length > 0
                    ) {
                        const outputFile = resultData.output_files[0];
                        const base64OutputData = outputFile.content_base64;
                        const decodedOutputDataBytesAsBuffer =
                            AiCadExecutionService.base64DecodeToBytes(base64OutputData);
                        const decodedOutputDataBytes = new Uint8Array(
                            decodedOutputDataBytesAsBuffer.buffer,
                            decodedOutputDataBytesAsBuffer.byteOffset,
                            decodedOutputDataBytesAsBuffer.byteLength,
                        );

                        if (!this.app.shapeConverter || !("convertFromSTEP" in this.app.shapeConverter)) {
                            Logger.error(
                                "AiCadExecutionService: IShapeConverter with convertFromSTEP is not available on app.",
                            );
                            return Result.err(
                                "CadQuery postprocessing error: Shape converter not available.",
                            );
                        }
                        const importedShapeResult = this.app.shapeConverter.convertFromSTEP(
                            doc,
                            decodedOutputDataBytesAsBuffer,
                        );

                        if (!importedShapeResult.isOk) {
                            Logger.error(
                                "AiCadExecutionService: Failed to convert STEP data to IShape:",
                                importedShapeResult.error,
                            );
                            return Result.err(
                                `CadQuery postprocessing error: Failed to import result: ${importedShapeResult.error}`,
                            );
                        }
                        // convertFromSTEP returns a FolderNode containing the imported geometry.
                        // We assume this function itself SHOULD NOT add the folderNode or its children to the active document directly
                        // in a way that makes it persistent. It should just return the geometry representation.
                        const folderNode = importedShapeResult.value;
                        Logger.info(
                            `AiCadExecutionService: convertFromSTEP returned FolderNode: ${folderNode.name}, ID: ${folderNode.id}`,
                        );

                        let finalShapeToRender: IShape | undefined;
                        const firstChildNode = folderNode.firstChild;

                        if (firstChildNode && firstChildNode instanceof ShapeNode) {
                            Logger.info(
                                `AiCadExecutionService: Extracted firstChildNode from FolderNode: ${firstChildNode.name}, ID: ${firstChildNode.id}, Type: ${firstChildNode.constructor.name}`,
                            );
                            if (firstChildNode.shape.isOk) {
                                finalShapeToRender = firstChildNode.shape.value;
                                Logger.info(
                                    "AiCadExecutionService: Successfully got IShape from firstChildNode to be rendered.",
                                );
                            } else {
                                Logger.error(
                                    "AiCadExecutionService: ShapeNode within imported FolderNode has an error state:",
                                    firstChildNode.shape.error,
                                );
                                return Result.err(
                                    `CadQuery postprocessing error: Imported shape node has error: ${firstChildNode.shape.error}`,
                                );
                            }
                        } else {
                            Logger.warn(
                                "AiCadExecutionService: No valid firstChildNode (ShapeNode) found in FolderNode. Attempting to use FolderNode's shape directly if it's a ShapeNode itself.",
                            );
                            // This case handles if the folderNode itself is a ShapeNode (e.g. if STEP contained a single unnamed part at root)
                            if (folderNode instanceof ShapeNode && folderNode.shape.isOk) {
                                finalShapeToRender = folderNode.shape.value;
                                Logger.info(
                                    "AiCadExecutionService: Using FolderNode's own shape as finalShapeToRender.",
                                );
                            } else {
                                Logger.error(
                                    "AiCadExecutionService: No valid shape found in imported STEP result (neither firstChild nor FolderNode itself).",
                                );
                                return Result.err(
                                    "CadQuery postprocessing error: No valid shape found in imported STEP result.",
                                );
                            }
                        }

                        // Create a new EditableShapeNode from the extracted IShape and add THIS new node to the document.
                        // This ensures the node is properly registered with the document's systems for persistence.
                        const nodeName = `ai_generated_${Date.now()}`;
                        const newNode = new EditableShapeNode(doc, nodeName, finalShapeToRender);

                        // Check if a materialRef was provided in the CadQuery directive.
                        if (parsedAiCode.materialRef && typeof parsedAiCode.materialRef === "string") {
                            const materialRef: string = parsedAiCode.materialRef;
                            Logger.info(
                                `AiCadExecutionService: Attempting to apply materialRef "${materialRef}" to CadQuery result.`,
                            );
                            try {
                                // Parse the materialRef string (e.g., "METALS.POLISHED_STEEL")
                                const materialMatch = materialRef.match(/^([A-Z_]+)\.([A-Z_]+)$/i); // Adjusted regex to allow underscores in category/preset

                                if (!materialMatch) {
                                    Logger.warn(
                                        `AiCadExecutionService: Invalid materialRef format "${materialRef}". Expected "CATEGORY.PRESET_NAME". Skipping material assignment.`,
                                    );
                                } else {
                                    const [_, categoryNameInput, presetNameInput] = materialMatch;
                                    const categoryName = categoryNameInput.toUpperCase();
                                    const presetName = presetNameInput.toUpperCase();

                                    // Find the MaterialCategory enum key.
                                    // MaterialCategory is an enum, e.g., MaterialCategory.Metal = "metal". We need to find the key ("Metal") from the value ("metal" or "METAL").
                                    const categoryKey = Object.keys(MaterialCategory).find(
                                        (key) =>
                                            MaterialCategory[
                                                key as keyof typeof MaterialCategory
                                            ].toUpperCase() === categoryName,
                                    );

                                    if (!categoryKey) {
                                        Logger.warn(
                                            `AiCadExecutionService: Unknown material category "${categoryName}" in materialRef "${materialRef}". Available categories: ${Object.values(MaterialCategory).join(", ")}. Skipping.`,
                                        );
                                    } else {
                                        // Access the presets from MaterialLibrary using the identified category name (e.g., MaterialLibrary.METALS)
                                        // The MaterialLibrary stores categories as uppercase keys like METALS, PLASTICS.
                                        const categoryPresets = (MaterialLibrary as any)[categoryName];

                                        if (!categoryPresets || typeof categoryPresets !== "object") {
                                            Logger.warn(
                                                `AiCadExecutionService: Could not find presets for category "${categoryName}". MaterialLibrary might not have this category. Skipping.`,
                                            );
                                        } else {
                                            // Find the preset key within the category (e.g., "POLISHED_STEEL")
                                            const presetKey = Object.keys(categoryPresets).find(
                                                (key) => key.toUpperCase() === presetName,
                                            );

                                            if (!presetKey) {
                                                Logger.warn(
                                                    `AiCadExecutionService: Unknown material preset "${presetName}" in category "${categoryName}". Available presets: ${Object.keys(categoryPresets).join(", ")}. Skipping.`,
                                                );
                                            } else {
                                                const materialPreset = (categoryPresets as any)[presetKey];
                                                // Create the material instance using the document and the found preset.
                                                const material = MaterialLibrary.createMaterial(
                                                    doc,
                                                    materialPreset,
                                                );
                                                // Add the newly created material to the document's material list.
                                                doc.materials.push(material);
                                                // Assign the material's ID to the new node.
                                                newNode.materialId = material.id;
                                                Logger.info(
                                                    `AiCadExecutionService: Successfully applied material '${material.name}' (ID: ${material.id}) to CadQuery result node '${newNode.name}'.`,
                                                );
                                            }
                                        }
                                    }
                                }
                            } catch (materialError: any) {
                                // Log any error during material application but don't let it break the shape creation.
                                Logger.error(
                                    `AiCadExecutionService: Error applying material "${materialRef}" to CadQuery result node: ${materialError.message || materialError}`,
                                    materialError,
                                );
                                // Non-fatal: The node will be added with the default material.
                            }
                        }

                        doc.addNode(newNode); // Explicitly add the new node to the document.
                        Logger.info(
                            `AiCadExecutionService: Explicitly added new EditableShapeNode '${newNode.name}' (ID: ${newNode.id}) to document for CadQuery result.`,
                        );

                        if (resultData.logs && resultData.logs.length > 0) {
                            Logger.info(
                                "AiCadExecutionService: Logs from CadQuery service:",
                                resultData.logs.join("\n"),
                            );
                        }

                        doc.visual.update();
                        // Save script and metadata to local storage.
                        if (this.app.storage) {
                            // Attempt to parse the exported variable name from the script.
                            let exportedVariableName = "unknown";
                            const exportMatch = payload.script.match(
                                /cq\.exporters\.export\s*\(\s*([^,]+?)\s*,\s*['"`]_internal_output\.(step|stl|gltf)['"`]/,
                            );
                            if (exportMatch && exportMatch[1]) {
                                exportedVariableName = exportMatch[1].trim();
                            }

                            // Prepare the record for storage.
                            // Keys are in snake_case to match the Supabase table 'CadQueryExecutions'.
                            // The 'id' of this record will be newNode.id (the ID of the created shape node).
                            // The 'user_id' will be automatically set by Supabase (DEFAULT auth.uid())
                            // based on the authenticated user making the request via HybridStorage/SupabaseStorage.
                            const scriptRecordData = {
                                python_script: payload.script, // Renamed from 'script'
                                document_id: doc.id, // Renamed from 'documentId'
                                shape_name: newNode.name, // Renamed from 'nodeName'
                                // 'created_node_id': newNode.id, // This is the primary key, passed separately to put()
                                exported_variable_name: exportedVariableName, // Renamed from 'exportedVariableName'
                                timestamp: new Date().toISOString(), // Changed from Date.now() to ISO string for TIMESTAMPTZ
                                source: "cadquery_ai_generation", // Remains 'source'
                            };
                            try {
                                // Store the record using the storage service.
                                // newNode.id is the primary key for the CadQueryExecutions table.
                                await this.app.storage.put(
                                    Constants.DBName,
                                    "CadQueryExecutions",
                                    newNode.id,
                                    scriptRecordData,
                                );
                                Logger.info(
                                    `Saved CadQuery script and metadata for node ID ${newNode.id} (exported var: ${exportedVariableName}) to storage.`,
                                );
                            } catch (storageError: any) {
                                Logger.error(
                                    `Failed to save CadQuery script for node ID ${newNode.id} to local storage:`,
                                    storageError,
                                );
                                // This is a non-fatal error; the shape was successfully created and added.
                            }
                        } else {
                            Logger.warn(
                                "Storage service not available on app. Cannot save script execution history.",
                            );
                        }
                        // Auto-save document to persistent storage (local or supabase) after node addition.
                        try {
                            await doc.save();
                            Logger.info(`Document saved after execution. Document ID: ${doc.id}`);
                        } catch (saveError) {
                            Logger.error(`Failed to save document after execution: ${saveError}`);
                        }
                        return Result.ok({
                            message: "CadQuery task executed and node added.",
                            node: newNode,
                        });
                    } else {
                        Logger.error(
                            "AiCadExecutionService: CadQuery service success response was malformed or had no output files.",
                            resultData,
                        );
                        return Result.err(
                            "CadQuery service error: Success response was malformed or had no output_files.",
                        );
                    }
                    // --------------- END OF CADQUERY DELEGATION LOGIC (TASK 1.3) ---------------
                } catch (cqProcessingError: any) {
                    // Catch any synchronous errors during CadQuery processing (e.g., from shape conversion, doc.addNode, etc.)
                    Logger.error(
                        "AiCadExecutionService: Error during CadQuery delegation internal processing:",
                        cqProcessingError,
                    );
                    return Result.err(
                        `CadQuery delegation processing failed: ${cqProcessingError.message || cqProcessingError}`,
                    );
                }
            }
        } catch (e) {
            // JSON.parse failed - if aiCode looks like a JSON directive, fail instead of JS exec.
            if (aiCode.trim().startsWith("{")) {
                Logger.error("AiCadExecutionService: Invalid JSON directive provided, cannot parse:", e);
                return Result.err(`Invalid JSON directive: ${e instanceof Error ? e.message : e}`);
            }
            // Not a JSON directive, proceed with normal TypeScript execution.
            // Logger.debug("AiCadExecutionService: Not a CadQuery JSON directive, proceeding with TypeScript execution.", e);
        }

        // Helper function to resolve various inputs (Node, IShape, variable name) to an IShape.
        const getShapeFromInput = (input: any): IShape | null => {
            let shape: IShape | null = null;
            let resolvedInput = input;
            const sourceDescription = typeof input === "string" ? `variable '${input}'` : "input object";

            try {
                // Resolve variable name first if input is a string.
                if (typeof input === "string") {
                    if (!this.storedVariables.has(input)) {
                        throw new Error(`Variable '${input}' not found.`);
                    }
                    resolvedInput = this.storedVariables.get(input);
                }

                // Check if it's a ShapeNode instance first.
                // This covers ParameterShapeNode and BooleanNode as they inherit from ShapeNode.
                if (resolvedInput instanceof ShapeNode && resolvedInput.shape instanceof Result) {
                    // Access the public 'shape' property which holds the Result<IShape>.
                    const shapeResult = resolvedInput.shape;
                    if (shapeResult.isOk) {
                        shape = shapeResult.value;
                    } else {
                        throw new Error(
                            `ShapeNode (${resolvedInput.constructor.name}) from ${sourceDescription} has an error: ${shapeResult.error}`,
                        );
                    }
                } else if (
                    resolvedInput &&
                    typeof resolvedInput.shapeType !== "undefined" &&
                    typeof resolvedInput.dispose === "function"
                ) {
                    // Duck-typing check for IShape interface (if input is already an IShape).
                    shape = resolvedInput as IShape;
                } else {
                    // Add more detail to the error if it's not a recognized type.
                    let inputType = typeof resolvedInput;
                    if (resolvedInput && resolvedInput.constructor) {
                        inputType = resolvedInput.constructor.name;
                    }
                    throw new Error(
                        `Input from ${sourceDescription} is not a recognized ShapeNode or IShape. Found type: ${inputType}.`,
                    );
                }

                // Final validation.
                if (!shape || shape.isNull()) {
                    throw new Error(`Resolved shape from ${sourceDescription} is null or invalid.`);
                }
                return shape;
            } catch (error: any) {
                // Rethrow error with context.
                Logger.error(`Error in getShapeFromInput for ${sourceDescription}:`, error);
                throw new Error(
                    `Failed to get valid IShape from ${sourceDescription}: ${error.message || error}`,
                );
            }
        };

        // Define the execution context. This object exposes specific application
        // functionalities and classes to the AI-generated code.
        const executionContext = {
            // Expose the current document instance.
            document: doc,
            // --- Core Types ---
            XYZ: XYZ,
            Plane: Plane,
            // Expose Ray for revolve axis definition.
            Ray: Ray,
            // Expose IShape type definition (useful for type hints in AI code).
            // --- Body Node Constructors ---
            ArcNode: ArcNode,
            // Expose BooleanNode constructor (primarily for storing results).
            BooleanNode: BooleanNode,
            // Expose BoxNode constructor.
            BoxNode: BoxNode,
            // Expose CircleNode constructor.
            CircleNode: CircleNode,
            // Expose ConeNode constructor.
            ConeNode: ConeNode,
            // Expose CylinderNode constructor.
            CylinderNode: CylinderNode,
            // Expose EllipseNode constructor.
            EllipseNode: EllipseNode,
            // Expose FaceNode constructor.
            FaceNode: FaceNode,
            // Expose FuseNode constructor.
            FuseNode: FuseNode,
            // Expose LineNode constructor.
            LineNode: LineNode,
            // Expose PolygonNode constructor.
            PolygonNode: PolygonNode,
            // Expose PrismNode constructor.
            PrismNode: PrismNode,
            // Expose PyramidNode constructor.
            PyramidNode: PyramidNode,
            // Expose RectNode constructor.
            RectNode: RectNode,
            // Expose RevolvedNode constructor.
            RevolvedNode: RevolvedNode,
            // Expose SweepedNode constructor.
            SweepedNode: SweepedNode,
            // Expose SphereNode constructor.
            SphereNode: SphereNode,
            // Expose WireNode constructor.
            WireNode: WireNode,
            // Expose EditableShapeNode if AI needs to wrap results manually.
            EditableShapeNode: EditableShapeNode,
            // --- Core Services/Utils ---
            Transaction: Transaction,
            Logger: Logger,
            PubSub: PubSub,
            // Expose MaterialLibrary for accessing presets.
            MaterialLibrary: MaterialLibrary,

            // --- Boolean Operations (Factory Wrappers) ---
            // Expose functions that perform boolean operations using the shape factory.
            booleanCommon: async (shape1Input: any, shape2Input: any): Promise<IShape> => {
                // Resolve inputs to IShape using the helper.
                const shape1 = getShapeFromInput(shape1Input);
                const shape2 = getShapeFromInput(shape2Input);
                if (!shape1 || !shape2) throw new Error("booleanCommon requires two valid shape inputs.");

                // Log input shape details before operation.
                Logger.info(
                    `booleanCommon: Input 1 - Type: ${shape1.shapeType}, IsNull: ${shape1.isNull()}`,
                );
                Logger.info(
                    `booleanCommon: Input 2 - Type: ${shape2.shapeType}, IsNull: ${shape2.isNull()}`,
                );

                // Call the shape factory method within try/catch.
                let result: Result<IShape>;
                try {
                    result = await doc.application.shapeFactory.booleanCommon(shape1, shape2);
                } catch (factoryError: any) {
                    Logger.error(
                        "booleanCommon: Error during shapeFactory.booleanCommon call:",
                        factoryError,
                    );
                    throw new Error(
                        `booleanCommon: Factory operation failed: ${factoryError.message || factoryError}`,
                    );
                }

                // Log result shape details.
                // Changed check from 'result.isErr' to '!result.isOk'.
                if (!result.isOk) {
                    Logger.error(`booleanCommon: Operation failed post-factory call: ${result.error}`);
                    throw new Error(`Boolean common failed: ${result.error}`);
                }
                Logger.info(
                    `booleanCommon: Result - Type: ${result.value.shapeType}, IsNull: ${result.value.isNull()}`,
                );

                // Check if result is a Compound, try to extract the solid.
                let finalShape = result.value;
                if (finalShape.shapeType === ShapeType.Compound) {
                    Logger.warn("booleanCommon resulted in a Compound. Attempting to extract Solid.");
                    const solids = finalShape.findSubShapes(ShapeType.Solid);
                    if (solids.length === 1) {
                        finalShape = solids[0];
                        Logger.info(`Extracted single Solid (Type: ${finalShape.shapeType}) from Compound.`);
                    } else {
                        Logger.error(
                            `Could not extract single Solid from Compound result (found ${solids.length}). Returning Compound.`,
                        );
                        // Optionally throw an error here instead of returning the compound
                        // throw new Error("Boolean common resulted in a Compound with zero or multiple Solids.");
                    }
                }

                // Return the resulting (potentially extracted) IShape.
                return finalShape;
            },
            booleanCut: async (shape1Input: any, shape2Input: any): Promise<IShape> => {
                const shape1 = getShapeFromInput(shape1Input);
                const shape2 = getShapeFromInput(shape2Input);
                if (!shape1 || !shape2) throw new Error("booleanCut requires two valid shape inputs.");

                // Log input shape details before operation.
                Logger.info(
                    `booleanCut: Input 1 (Tool) - Type: ${shape1.shapeType}, IsNull: ${shape1.isNull()}`,
                );
                Logger.info(
                    `booleanCut: Input 2 (Object) - Type: ${shape2.shapeType}, IsNull: ${shape2.isNull()}`,
                );

                // Call the shape factory method within try/catch.
                let result: Result<IShape>;
                try {
                    result = await doc.application.shapeFactory.booleanCut(shape1, shape2);
                } catch (factoryError: any) {
                    Logger.error("booleanCut: Error during shapeFactory.booleanCut call:", factoryError);
                    throw new Error(
                        `booleanCut: Factory operation failed: ${factoryError.message || factoryError}`,
                    );
                }

                // Log result shape details.
                // Changed check from 'result.isErr' to '!result.isOk'.
                if (!result.isOk) {
                    Logger.error(`booleanCut: Operation failed post-factory call: ${result.error}`);
                    throw new Error(`Boolean cut failed: ${result.error}`);
                }
                Logger.info(
                    `booleanCut: Result - Type: ${result.value.shapeType}, IsNull: ${result.value.isNull()}`,
                );

                // Check if result is a Compound, try to extract the solid.
                let finalShape = result.value;
                if (finalShape.shapeType === ShapeType.Compound) {
                    Logger.warn("booleanCut resulted in a Compound. Attempting to extract Solid.");
                    const solids = finalShape.findSubShapes(ShapeType.Solid);
                    if (solids.length === 1) {
                        finalShape = solids[0];
                        Logger.info(`Extracted single Solid (Type: ${finalShape.shapeType}) from Compound.`);
                    } else {
                        Logger.error(
                            `Could not extract single Solid from Compound result (found ${solids.length}). Returning Compound.`,
                        );
                        // Optionally throw an error here instead of returning the compound
                        // throw new Error("Boolean cut resulted in a Compound with zero or multiple Solids.");
                    }
                }

                return finalShape;
            },
            booleanFuse: async (shape1Input: any, shape2Input: any): Promise<IShape> => {
                const shape1 = getShapeFromInput(shape1Input);
                const shape2 = getShapeFromInput(shape2Input);
                if (!shape1 || !shape2) throw new Error("booleanFuse requires two valid shape inputs.");

                // Log input shape details before operation.
                Logger.info(`booleanFuse: Input 1 - Type: ${shape1.shapeType}, IsNull: ${shape1.isNull()}`);
                Logger.info(`booleanFuse: Input 2 - Type: ${shape2.shapeType}, IsNull: ${shape2.isNull()}`);

                // Call the shape factory method within try/catch.
                let result: Result<IShape>;
                try {
                    result = await doc.application.shapeFactory.booleanFuse(shape1, shape2);
                } catch (factoryError: any) {
                    Logger.error("booleanFuse: Error during shapeFactory.booleanFuse call:", factoryError);
                    throw new Error(
                        `booleanFuse: Factory operation failed: ${factoryError.message || factoryError}`,
                    );
                }

                // Log result shape details.
                // Changed check from 'result.isErr' to '!result.isOk'.
                if (!result.isOk) {
                    Logger.error(`booleanFuse: Operation failed post-factory call: ${result.error}`);
                    throw new Error(`Boolean fuse failed: ${result.error}`);
                }
                Logger.info(
                    `booleanFuse: Result - Type: ${result.value.shapeType}, IsNull: ${result.value.isNull()}`,
                );

                // Check if result is a Compound, try to extract the solid.
                let finalShape = result.value;
                if (finalShape.shapeType === ShapeType.Compound) {
                    Logger.warn("booleanFuse resulted in a Compound. Attempting to extract Solid.");
                    const solids = finalShape.findSubShapes(ShapeType.Solid);
                    if (solids.length === 1) {
                        finalShape = solids[0];
                        Logger.info(`Extracted single Solid (Type: ${finalShape.shapeType}) from Compound.`);
                    } else {
                        Logger.error(
                            `Could not extract single Solid from Compound result (found ${solids.length}). Returning Compound.`,
                        );
                        // Optionally throw an error here instead of returning the compound
                        // throw new Error("Boolean fuse resulted in a Compound with zero or multiple Solids.");
                    }
                }

                return finalShape;
            },

            // --- Modification Operations (Factory Wrappers) ---
            // Expose function for simple thick solid creation.
            makeThickSolidSimple: async (shapeInput: any, thickness: number): Promise<IShape> => {
                // Resolve base shape.
                const baseShape = getShapeFromInput(shapeInput);
                if (!baseShape) throw new Error("makeThickSolidSimple requires a valid base shape input.");
                // Call the shape factory method.
                const result = await doc.application.shapeFactory.makeThickSolidBySimple(
                    baseShape,
                    thickness,
                );
                // Check result and return IShape or throw.
                if (!result.isOk) {
                    throw new Error(`Make thick solid failed: ${result.error}`);
                }
                return result.value;
            },

            // Expose function to chamfer all edges of a shape.
            chamferAllEdges: async (shapeInput: any, distance: number): Promise<IShape> => {
                // Resolve the input (Node instance or variable name) to an IShape.
                const baseShape = getShapeFromInput(shapeInput);
                if (!baseShape) throw new Error("chamferAllEdges requires a valid base shape input.");

                // Find all edges of the base shape.
                const edges = baseShape.findSubShapes(ShapeType.Edge) as IEdge[];
                if (!edges || edges.length === 0) {
                    throw new Error("No edges found on the provided shape to chamfer.");
                }

                // Call the shape factory method to perform the chamfer.
                const result = await doc.application.shapeFactory.chamfer(baseShape, edges, distance);

                // Check the result and return the new IShape or throw an error.
                if (!result.isOk) {
                    throw new Error(`Chamfer operation failed: ${result.error}`);
                }
                return result.value;
            },

            // Expose function to fillet all edges of a shape.
            filletAllEdges: async (shapeInput: any, radius: number): Promise<IShape> => {
                // Resolve the input (Node instance or variable name) to an IShape.
                const baseShape = getShapeFromInput(shapeInput);
                if (!baseShape) throw new Error("filletAllEdges requires a valid base shape input.");

                // Find all edges of the base shape.
                const edges = baseShape.findSubShapes(ShapeType.Edge) as IEdge[];
                if (!edges || edges.length === 0) {
                    throw new Error("No edges found on the provided shape to fillet.");
                }

                // Call the shape factory method to perform the fillet.
                const result = await doc.application.shapeFactory.fillet(baseShape, edges, radius);

                // Check the result and return the new IShape or throw an error.
                if (!result.isOk) {
                    throw new Error(`Fillet operation failed: ${result.error}`);
                }
                return result.value;
            },

            // --- Variable Storage ---
            // Expose functions to store and retrieve variables within the service instance.
            storeVariable: (name: string, value: any): any => {
                if (!/^[a-zA-Z_$][a-zA-Z0-9_$]*$/.test(name)) {
                    throw new Error(
                        `Invalid variable name: \"${name}\". Names must follow JavaScript identifier rules.`,
                    );
                }
                this.storedVariables.set(name, value);
                Logger.info(`Stored variable '${name}'`);
                return value;
            },
            getVariable: (name: string): any => {
                if (!this.storedVariables.has(name)) {
                    throw new Error(`Variable '${name}' not found.`);
                }
                Logger.info(`Retrieved variable '${name}'`);
                return this.storedVariables.get(name);
            },
            listVariables: (): string[] => {
                return Array.from(this.storedVariables.keys());
            },

            // --- Document Interaction ---
            // Enhanced addNode to accept IShape directly.
            addNode: (nodeOrShapeToAdd: any, name?: string): ShapeNode => {
                let nodeToAdd: ShapeNode;
                // Check if input is already a valid ShapeNode instance.
                if (nodeOrShapeToAdd instanceof ShapeNode) {
                    nodeToAdd = nodeOrShapeToAdd;
                    // Assign name if provided and node name is default.
                    if (name && nodeToAdd.name.startsWith("Node_")) {
                        // Check if name is default/unset
                        nodeToAdd.name = name;
                    }
                }
                // Check if input is an IShape (using duck typing).
                else if (
                    nodeOrShapeToAdd &&
                    typeof nodeOrShapeToAdd.shapeType !== "undefined" &&
                    typeof nodeOrShapeToAdd.dispose === "function"
                ) {
                    // Wrap the IShape in an EditableShapeNode.
                    const shape = nodeOrShapeToAdd as IShape;
                    // Log the type of shape being added directly.
                    Logger.info(
                        `addNode: Wrapping raw IShape (Type: ${shape.shapeType}, IsNull: ${shape.isNull()}) in EditableShapeNode.`,
                    );
                    // Use provided name or generate a default one.
                    const nodeName = name || I18n.translate("body.editableShape"); // Provide a default name
                    nodeToAdd = new EditableShapeNode(doc, nodeName, shape);
                }
                // If input is neither Node nor IShape, throw error.
                else {
                    throw new Error(
                        "Invalid object passed to addNode. Expected a ShapeNode instance or an IShape object.",
                    );
                }

                // Add the resolved/created node to the document.
                doc.addNode(nodeToAdd);
                Logger.info(`Added node '${nodeToAdd.name}' to document.`);
                return nodeToAdd; // Return the added node
            },

            // --- Debugging / Inspection Helpers ---
            // Helper to log shape details from a variable or direct node/shape.
            logShapeDetails: (input: any, label: string = "Shape") => {
                let shape: IShape | null = null;
                let nodeType = "N/A";
                let resolvedInput = input;
                const sourceDescription = typeof input === "string" ? `variable '${input}'` : "input object";

                try {
                    // Resolve variable name first if input is a string.
                    if (typeof input === "string") {
                        if (!this.storedVariables.has(input)) {
                            throw new Error(`Variable '${input}' not found.`);
                        }
                        resolvedInput = this.storedVariables.get(input);
                    }

                    // Check if it's a Node instance
                    if (resolvedInput instanceof ShapeNode) {
                        nodeType = resolvedInput.constructor.name;
                        const shapeResult = resolvedInput.shape;
                        if (shapeResult.isOk) {
                            shape = shapeResult.value;
                        } else {
                            Logger.error(
                                `${label} (${sourceDescription} - ${nodeType}): Node contains error: ${shapeResult.error}`,
                            );
                            return; // Don't proceed if node has error
                        }
                    }
                    // Check if it's already an IShape
                    else if (resolvedInput && typeof resolvedInput.shapeType !== "undefined") {
                        nodeType = "(Raw IShape)";
                        shape = resolvedInput as IShape;
                    } else {
                        throw new Error(
                            `Input from ${sourceDescription} is not a recognized Node or IShape.`,
                        );
                    }

                    // Log details if shape was resolved
                    if (shape) {
                        Logger.info(
                            `${label} (${sourceDescription} - ${nodeType}): Type = ${shape.shapeType}, IsNull = ${shape.isNull()}, IsClosed = ${shape.isClosed()}`,
                        );
                    } else {
                        Logger.warn(
                            `${label} (${sourceDescription} - ${nodeType}): Could not resolve to a valid IShape.`,
                        );
                    }
                } catch (error: any) {
                    Logger.error(
                        `Error in logShapeDetails for ${label} (${sourceDescription}): ${error.message || error}`,
                    );
                }
            },

            // --- Material Assignment ---
            // Function to assign a predefined material to a ShapeNode.
            assignMaterial: (targetInput: string | ShapeNode, materialRef: string): void => {
                // Validate material reference string format.
                const materialMatch = materialRef.match(/^([A-Z]+)\.([A-Z_]+)$/i);
                if (!materialMatch) {
                    throw new Error(
                        `Invalid material reference format: "${materialRef}". Expected format: CATEGORY.PRESET_NAME (e.g., METALS.POLISHED_STEEL).`,
                    );
                }
                const [_, categoryName, presetName] = materialMatch;

                // Find the material category in MaterialLibrary.
                const categoryKey = Object.keys(MaterialCategory).find(
                    (key) =>
                        MaterialCategory[key as keyof typeof MaterialCategory].toUpperCase() ===
                        categoryName.toUpperCase(),
                );
                if (!categoryKey) {
                    throw new Error(
                        `Unknown material category: "${categoryName}". Available categories: ${Object.values(MaterialCategory).join(", ")}.`,
                    );
                }
                const categoryValue = MaterialCategory[categoryKey as keyof typeof MaterialCategory];

                // Access the presets within the category. Use bracket notation for dynamic access.
                const categoryPresets =
                    MaterialLibrary[categoryName.toUpperCase() as keyof typeof MaterialLibrary]; // e.g., MaterialLibrary.METALS
                if (!categoryPresets || typeof categoryPresets !== "object") {
                    // This case should ideally not happen if categoryKey was found, but good for safety.
                    throw new Error(
                        `Internal error: Could not find presets for category "${categoryName}".`,
                    );
                }

                // Find the specific material preset within the category (case-insensitive).
                const presetKey = Object.keys(categoryPresets).find(
                    (key) => key.toUpperCase() === presetName.toUpperCase(),
                );
                if (!presetKey) {
                    const availablePresets = Object.keys(categoryPresets).join(", ");
                    throw new Error(
                        `Unknown material preset: "${presetName}" in category "${categoryName}". Available presets: ${availablePresets}.`,
                    );
                }
                const materialPreset = categoryPresets[presetKey as keyof typeof categoryPresets];

                // Create the material instance using the library function.
                const material = MaterialLibrary.createMaterial(doc, materialPreset);
                // Add the newly created material to the document's material collection.
                doc.materials.push(material);

                // Resolve the target node. It must be a ShapeNode.
                let targetNode: ShapeNode;
                if (typeof targetInput === "string") {
                    // Input is a variable name, retrieve it.
                    const variableValue = this.storedVariables.get(targetInput);
                    if (!variableValue) {
                        throw new Error(`Variable "${targetInput}" not found.`);
                    }
                    if (!(variableValue instanceof ShapeNode)) {
                        throw new Error(
                            `Variable "${targetInput}" does not hold a ShapeNode, cannot assign material.`,
                        );
                    }
                    targetNode = variableValue;
                } else if (targetInput instanceof ShapeNode) {
                    // Input is already a ShapeNode instance.
                    targetNode = targetInput;
                } else {
                    throw new Error(
                        "Invalid target for assignMaterial. Expected a ShapeNode instance or the name of a variable holding one.",
                    );
                }

                // Assign the material ID to the node's property.
                targetNode.materialId = material.id;
                Logger.info(
                    `Assigned material '${material.name}' (ID: ${material.id}) to node '${targetNode.name}'.`,
                );

                // Trigger a visual update to reflect the material change.
                doc.visual.update();
            },

            // --- Future additions ---
            // Placeholder: All basic bodys nodes are now added.
            // Placeholder: We might expose utility functions (e.g., createPlane) later.
        };

        try {
            // Use the AsyncFunction constructor to safely execute the AI code.
            // This creates an async function in a restricted scope.
            // The keys of 'executionContext' become the names of arguments available inside the function.
            const AsyncFunction = Object.getPrototypeOf(async function () {}).constructor;
            // The generated function takes arguments named after the keys in executionContext.
            // The last argument is the AI code string itself.
            const func = new AsyncFunction(...Object.keys(executionContext), aiCode);

            // Execute the generated function using 'apply'.
            // 'undefined' is passed as 'this' context to prevent the AI code from accessing the service's 'this'.
            // The values from 'executionContext' are passed as arguments.
            const scriptResult = await func.apply(undefined, Object.values(executionContext));

            // Log successful execution and the result returned by the script (if any).
            Logger.info(
                `AiCadExecutionService: Code executed successfully. Script returned: ${typeof scriptResult === "object" ? JSON.stringify(scriptResult) : scriptResult}`,
            );

            // Update the visual representation after successful execution.
            doc.visual.update();

            // Auto-save document after successful AI code execution.
            try {
                await doc.save();
                Logger.info(`Document saved after AI code execution. Document ID: ${doc.id}`);
            } catch (saveError) {
                Logger.error(`Failed to save document after AI code execution: ${saveError}`);
            }

            // Return success indication along with the script's return value.
            return Result.ok(scriptResult ?? { message: "AI code executed successfully." });
        } catch (error: any) {
            // Catch and log any errors occurring during the execution of the AI code.
            Logger.error("AiCadExecutionService: Error executing AI code:", error);
            // Return an error Result with a descriptive message.
            return Result.err(`AI code execution failed: ${error.message || error}`);
        }
    }

    // --- Private Helper for CadQuery Execution ---
    /**
     * Handles the specific logic for executing a CadQuery task.
     * Sends the request to the CadQuery service and processes the response.
     * Assumes parsedAiCode is a valid CadQuery directive object.
     * @param parsedAiCode The parsed JSON object representing the CadQuery task.
     * @param doc The active document.
     * @returns Result containing the resulting node on success, or an error string on failure.
     */
    private async _executeCadQueryTask(
        parsedAiCode: any,
        doc: import("luminicad-core").IDocument,
    ): Promise<Result<any, string>> {
        // Extracted CadQuery logic starts here.
        try {
            Logger.info("AiCadExecutionService: Delegating to CadQuery service...");

            const {
                script,
                input_files: inputFiles,
                expected_output_format: expectedOutputFormat,
            } = parsedAiCode;

            if (!script || typeof script !== "string") {
                Logger.error(
                    "AiCadExecutionService: CadQuery directive is missing 'script' or it's not a string.",
                );
                return Result.err("CadQuery directive error: 'script' is missing or invalid.");
            }

            const cadQueryServicePayloadInputFiles: { filename: string; content_base64: string }[] = [];

            // Use static helper methods for base64 operations
            if (inputFiles && Array.isArray(inputFiles)) {
                for (const inputFile of inputFiles) {
                    if (!inputFile.variable_name || !inputFile.output_filename_for_script) {
                        Logger.warn(
                            "AiCadExecutionService: Skipping invalid input_file entry in CadQuery directive:",
                            inputFile,
                        );
                        continue;
                    }
                    const luminiCadObject = this.storedVariables.get(inputFile.variable_name);
                    if (!luminiCadObject) {
                        throw new Error(
                            `Variable '${inputFile.variable_name}' not found in storedVariables.`,
                        );
                    }
                    if (!(luminiCadObject instanceof ShapeNode)) {
                        throw new Error(`Variable '${inputFile.variable_name}' is not a ShapeNode.`);
                    }
                    const shapeToExportResult = luminiCadObject.shape;
                    if (!shapeToExportResult.isOk) {
                        throw new Error(
                            `Error getting shape from '${inputFile.variable_name}': ${shapeToExportResult.error}`,
                        );
                    }
                    const shapeToExport = shapeToExportResult.value;
                    if (!this.app.shapeConverter || !("convertToSTEP" in this.app.shapeConverter)) {
                        throw new Error("IShapeConverter with convertToSTEP is not available on app.");
                    }
                    const stepResult = this.app.shapeConverter.convertToSTEP(shapeToExport);
                    if (!stepResult.isOk) {
                        throw new Error(
                            `Failed to convert '${inputFile.variable_name}' to STEP: ${stepResult.error}`,
                        );
                    }
                    const stepDataString = stepResult.value;
                    // Use static method for encoding
                    const base64EncodedStepString = AiCadExecutionService.base64Encode(stepDataString);
                    cadQueryServicePayloadInputFiles.push({
                        filename: inputFile.output_filename_for_script,
                        content_base64: base64EncodedStepString,
                    });
                    Logger.info(
                        `AiCadExecutionService: Prepared input file '${inputFile.output_filename_for_script}' from variable '${inputFile.variable_name}'.`,
                    );
                }
            }

            const cadQueryServiceUrl = AI_CONFIG.CADQUERY_SERVICE_URL;
            const payload = {
                script: script,
                input_files: cadQueryServicePayloadInputFiles,
                output_format: expectedOutputFormat || "step",
            };

            Logger.info("AiCadExecutionService: Sending POST to CadQuery service...", {
                url: cadQueryServiceUrl,
                payload: { ...payload, script: payload.script.substring(0, 100) + "..." },
            }); // Log truncated script

            let response;
            try {
                response = await fetch(cadQueryServiceUrl, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(payload),
                });
                Logger.info(
                    `AiCadExecutionService: Received response from CadQuery service with status: ${response.status}`,
                );
            } catch (networkError: any) {
                Logger.error("AiCadExecutionService: Network error calling CadQuery service:", networkError);
                return Result.err(`CadQuery service network error: ${networkError.message}`);
            }

            if (!response.ok) {
                const errorText = await response.text();
                Logger.error(
                    `AiCadExecutionService: CadQuery service returned error ${response.status}:`,
                    errorText,
                );
                try {
                    const errorJson = JSON.parse(errorText);
                    return Result.err(
                        `CadQuery service error (${response.status}): ${errorJson.message || errorText}. Logs: ${JSON.stringify(errorJson.logs)}`,
                    );
                } catch {
                    return Result.err(`CadQuery service error (${response.status}): ${errorText}`);
                }
            }

            const resultData = await response.json();
            Logger.info("AiCadExecutionService: Parsed JSON response from CadQuery service:", resultData);

            if (resultData.status === "error") {
                Logger.error(
                    "AiCadExecutionService: CadQuery service reported an execution error:",
                    resultData.message,
                    resultData.logs,
                );
                // Combine message and logs for feedback
                const errorDetail = `Message: ${resultData.message}. Logs: ${JSON.stringify(resultData.logs)}`;
                return Result.err(`CadQuery script execution error: ${errorDetail}`);
            }

            if (
                resultData.status === "success" &&
                resultData.output_files &&
                resultData.output_files.length > 0
            ) {
                const outputFile = resultData.output_files[0];
                const base64OutputData = outputFile.content_base64;
                // Use static method for decoding
                const decodedOutputDataBytes = AiCadExecutionService.base64DecodeToBytes(base64OutputData);

                if (!this.app.shapeConverter || !("convertFromSTEP" in this.app.shapeConverter)) {
                    throw new Error("IShapeConverter with convertFromSTEP is not available on app.");
                }
                const importedShapeResult = this.app.shapeConverter.convertFromSTEP(
                    doc,
                    decodedOutputDataBytes,
                );

                if (!importedShapeResult.isOk) {
                    throw new Error(`Failed to convert STEP data to IShape: ${importedShapeResult.error}`);
                }

                const folderNode = importedShapeResult.value;
                Logger.info(
                    `AiCadExecutionService: convertFromSTEP returned FolderNode: ${folderNode.name}, ID: ${folderNode.id}`,
                );

                let finalShapeToRender: IShape | undefined;
                const firstChildNode = folderNode.firstChild;

                if (firstChildNode && firstChildNode instanceof ShapeNode) {
                    Logger.info(
                        `AiCadExecutionService: Extracted firstChildNode: ${firstChildNode.name}, ID: ${firstChildNode.id}, Type: ${firstChildNode.constructor.name}`,
                    );
                    if (firstChildNode.shape.isOk) {
                        finalShapeToRender = firstChildNode.shape.value;
                        Logger.info("AiCadExecutionService: Got IShape from firstChildNode.");
                    } else {
                        throw new Error(`Imported shape node has error: ${firstChildNode.shape.error}`);
                    }
                } else {
                    Logger.warn(
                        "AiCadExecutionService: No valid firstChildNode found. Using FolderNode's shape if possible.",
                    );
                    if (folderNode instanceof ShapeNode && folderNode.shape.isOk) {
                        finalShapeToRender = folderNode.shape.value;
                        Logger.info("AiCadExecutionService: Using FolderNode's own shape.");
                    } else {
                        throw new Error("No valid shape found in imported STEP result.");
                    }
                }

                const nodeName = `ai_generated_${Date.now()}`;
                const newNode = new EditableShapeNode(doc, nodeName, finalShapeToRender);

                // Check if a materialRef was provided in the CadQuery directive.
                if (parsedAiCode.materialRef && typeof parsedAiCode.materialRef === "string") {
                    const materialRef: string = parsedAiCode.materialRef;
                    Logger.info(
                        `AiCadExecutionService: Attempting to apply materialRef "${materialRef}" to CadQuery result.`,
                    );
                    try {
                        // Parse the materialRef string (e.g., "METALS.POLISHED_STEEL")
                        const materialMatch = materialRef.match(/^([A-Z_]+)\.([A-Z_]+)$/i); // Adjusted regex to allow underscores in category/preset

                        if (!materialMatch) {
                            Logger.warn(
                                `AiCadExecutionService: Invalid materialRef format "${materialRef}". Expected "CATEGORY.PRESET_NAME". Skipping material assignment.`,
                            );
                        } else {
                            const [_, categoryNameInput, presetNameInput] = materialMatch;
                            const categoryName = categoryNameInput.toUpperCase();
                            const presetName = presetNameInput.toUpperCase();

                            // Find the MaterialCategory enum key.
                            // MaterialCategory is an enum, e.g., MaterialCategory.Metal = "metal". We need to find the key ("Metal") from the value ("metal" or "METAL").
                            const categoryKey = Object.keys(MaterialCategory).find(
                                (key) =>
                                    MaterialCategory[key as keyof typeof MaterialCategory].toUpperCase() ===
                                    categoryName,
                            );

                            if (!categoryKey) {
                                Logger.warn(
                                    `AiCadExecutionService: Unknown material category "${categoryName}" in materialRef "${materialRef}". Available categories: ${Object.values(MaterialCategory).join(", ")}. Skipping.`,
                                );
                            } else {
                                // Access the presets from MaterialLibrary using the identified category name (e.g., MaterialLibrary.METALS)
                                // The MaterialLibrary stores categories as uppercase keys like METALS, PLASTICS.
                                const categoryPresets = (MaterialLibrary as any)[categoryName];

                                if (!categoryPresets || typeof categoryPresets !== "object") {
                                    Logger.warn(
                                        `AiCadExecutionService: Could not find presets for category "${categoryName}". MaterialLibrary might not have this category. Skipping.`,
                                    );
                                } else {
                                    // Find the preset key within the category (e.g., "POLISHED_STEEL")
                                    const presetKey = Object.keys(categoryPresets).find(
                                        (key) => key.toUpperCase() === presetName,
                                    );

                                    if (!presetKey) {
                                        Logger.warn(
                                            `AiCadExecutionService: Unknown material preset "${presetName}" in category "${categoryName}". Available presets: ${Object.keys(categoryPresets).join(", ")}. Skipping.`,
                                        );
                                    } else {
                                        const materialPreset = (categoryPresets as any)[presetKey];
                                        // Create the material instance using the document and the found preset.
                                        const material = MaterialLibrary.createMaterial(doc, materialPreset);
                                        // Add the newly created material to the document's material list.
                                        doc.materials.push(material);
                                        // Assign the material's ID to the new node.
                                        newNode.materialId = material.id;
                                        Logger.info(
                                            `AiCadExecutionService: Successfully applied material '${material.name}' (ID: ${material.id}) to CadQuery result node '${newNode.name}'.`,
                                        );
                                    }
                                }
                            }
                        }
                    } catch (materialError: any) {
                        // Log any error during material application but don't let it break the shape creation.
                        Logger.error(
                            `AiCadExecutionService: Error applying material "${materialRef}" to CadQuery result node: ${materialError.message || materialError}`,
                            materialError,
                        );
                        // Non-fatal: The node will be added with the default material.
                    }
                }

                doc.addNode(newNode); // Explicitly add the new node to the document.
                Logger.info(
                    `AiCadExecutionService: Added new EditableShapeNode '${newNode.name}' (ID: ${newNode.id}) to document.`,
                );

                if (resultData.logs && resultData.logs.length > 0) {
                    Logger.info(
                        "AiCadExecutionService: Logs from CadQuery service:",
                        resultData.logs.join("\n"),
                    );
                }

                doc.visual.update();
                // Save script and metadata to local storage.
                if (this.app.storage) {
                    // Attempt to parse the exported variable name from the script.
                    let exportedVariableName = "unknown";
                    const exportMatch = payload.script.match(
                        /cq\.exporters\.export\s*\(\s*([^,]+?)\s*,\s*['"`]_internal_output\.(step|stl|gltf)['"`]/,
                    );
                    if (exportMatch && exportMatch[1]) {
                        exportedVariableName = exportMatch[1].trim();
                    }

                    // Prepare the record for storage.
                    // Keys are in snake_case to match the Supabase table 'CadQueryExecutions'.
                    // The 'id' of this record will be newNode.id (the ID of the created shape node).
                    // The 'user_id' will be automatically set by Supabase (DEFAULT auth.uid())
                    // based on the authenticated user making the request via HybridStorage/SupabaseStorage.
                    const scriptRecordData = {
                        python_script: payload.script, // Renamed from 'script'
                        document_id: doc.id, // Renamed from 'documentId'
                        shape_name: newNode.name, // Renamed from 'nodeName'
                        // 'created_node_id': newNode.id, // This is the primary key, passed separately to put()
                        exported_variable_name: exportedVariableName, // Renamed from 'exportedVariableName'
                        timestamp: new Date().toISOString(), // Changed from Date.now() to ISO string for TIMESTAMPTZ
                        source: "cadquery_ai_generation", // Remains 'source'
                    };
                    try {
                        // Store the record using the storage service.
                        // newNode.id is the primary key for the CadQueryExecutions table.
                        await this.app.storage.put(
                            Constants.DBName,
                            "CadQueryExecutions",
                            newNode.id,
                            scriptRecordData,
                        );
                        Logger.info(
                            `Saved CadQuery script and metadata for node ID ${newNode.id} (exported var: ${exportedVariableName}) to storage.`,
                        );
                    } catch (storageError: any) {
                        Logger.error(
                            `Failed to save CadQuery script for node ID ${newNode.id} to local storage:`,
                            storageError,
                        );
                        // This is a non-fatal error; the shape was successfully created and added.
                    }
                } else {
                    Logger.warn(
                        "Storage service not available on app. Cannot save CadQuery script execution history.",
                    );
                }
                // Auto-save document to persistent storage (local or supabase) after node addition.
                try {
                    await doc.save();
                    Logger.info(`Document saved after CadQuery execution. Document ID: ${doc.id}`);
                } catch (saveError) {
                    Logger.error(`Failed to save document after CadQuery execution: ${saveError}`);
                }
                return Result.ok({ message: "CadQuery task executed and node added.", node: newNode });
            } else {
                Logger.error(
                    "AiCadExecutionService: CadQuery service success response was malformed or had no output files.",
                    resultData,
                );
                return Result.err(
                    "CadQuery service error: Success response was malformed or had no output_files.",
                );
            }
        } catch (processingError: any) {
            // Catch synchronous errors during processing (e.g., from shape conversion, node creation, doc access)
            Logger.error(
                "AiCadExecutionService: Error during CadQuery task internal processing:",
                processingError,
            );
            return Result.err(
                `CadQuery task processing failed: ${processingError.message || processingError}`,
            );
        }
    }
}
